import { DateTime } from 'luxon';

// Default timezone for the application
const DEFAULT_TIMEZONE = 'Europe/London';

export const formatDate = (date: Date): string => {
  return DateTime.fromJSDate(date, { zone: DEFAULT_TIMEZONE }).toISODate() || '';
};

export const formatTime = (date: Date): string => {
  return DateTime.fromJSDate(date, { zone: DEFAULT_TIMEZONE }).toFormat('HH:mm');
};

export const formatDisplayDate = (date: Date): string => {
  return DateTime.fromJSDate(date, { zone: DEFAULT_TIMEZONE }).toFormat('MMM dd, yyyy');
};

export const formatDisplayTime = (date: Date): string => {
  return DateTime.fromJSDate(date, { zone: DEFAULT_TIMEZONE }).toFormat('h:mm a');
};

export const getDaysInMonth = (date: Date): Date[] => {
  const luxonDate = DateTime.fromJSDate(date, { zone: DEFAULT_TIMEZONE });
  const startOfMonth = luxonDate.startOf('month');
  const endOfMonth = luxonDate.endOf('month');
  
  const days: Date[] = [];
  let current = startOfMonth;
  
  while (current <= endOfMonth) {
    days.push(current.toJSDate());
    current = current.plus({ days: 1 });
  }
  
  return days;
};

export const isCurrentMonth = (date: Date): boolean => {
  const luxonDate = DateTime.fromJSDate(date, { zone: DEFAULT_TIMEZONE });
  const now = DateTime.now().setZone(DEFAULT_TIMEZONE);
  return luxonDate.hasSame(now, 'month') && luxonDate.hasSame(now, 'year');
};

export const isCurrentDay = (date: Date): boolean => {
  const luxonDate = DateTime.fromJSDate(date, { zone: DEFAULT_TIMEZONE });
  const now = DateTime.now().setZone(DEFAULT_TIMEZONE);
  return luxonDate.hasSame(now, 'day') && luxonDate.hasSame(now, 'month') && luxonDate.hasSame(now, 'year');
};

export const addMonth = (date: Date): Date => {
  return DateTime.fromJSDate(date, { zone: DEFAULT_TIMEZONE }).plus({ months: 1 }).toJSDate();
};

export const subtractMonth = (date: Date): Date => {
  return DateTime.fromJSDate(date, { zone: DEFAULT_TIMEZONE }).minus({ months: 1 }).toJSDate();
};

/**
 * Calculate hours between two time strings in HH:mm format.
 * Times are interpreted as Europe/London time on the given date.
 * Returns the duration in hours as a decimal number.
 */
export const calculateHours = (clockIn: string, clockOut: string, date?: Date): number => {
  const baseDate = date || new Date();
  const dateStr = formatDate(baseDate);
  
  // Create DateTime objects in London timezone
  const clockInDateTime = DateTime.fromISO(`${dateStr}T${clockIn}`, { zone: DEFAULT_TIMEZONE });
  const clockOutDateTime = DateTime.fromISO(`${dateStr}T${clockOut}`, { zone: DEFAULT_TIMEZONE });
  
  // If times are the same, return 0 hours
  if (clockIn === clockOut) {
    return 0;
  }
  
  // Handle overnight shifts (clock out is next day)
  if (clockOutDateTime <= clockInDateTime) {
    const nextDay = clockOutDateTime.plus({ days: 1 });
    const duration = nextDay.diff(clockInDateTime, 'hours');
    return Math.round(duration.hours * 100) / 100; // Round to 2 decimal places
  }
  
  const duration = clockOutDateTime.diff(clockInDateTime, 'hours');
  return Math.round(duration.hours * 100) / 100; // Round to 2 decimal places
};

export const validateTimeFormat = (time: string): boolean => {
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return timeRegex.test(time);
};

export const getMonthName = (date: Date): string => {
  return DateTime.fromJSDate(date, { zone: DEFAULT_TIMEZONE }).toFormat('MMMM yyyy');
};

/**
 * Convert a time (HH:mm) to UTC ISO string for storage.
 * The time input from HTML is treated as London time (what the user sees on their clock in London).
 * This is used when saving shift data to the database.
 */
export const convertLondonTimeToUTC = (time: string, date: Date): string => {
  if (!time || time.trim() === '') {
    console.error('Empty time provided to convertLondonTimeToUTC');
    return '';
  }
  
  const dateStr = formatDate(date);
  
  // Create DateTime in London timezone (treating the input time as London time)
  const londonDateTime = DateTime.fromISO(`${dateStr}T${time}`, { zone: DEFAULT_TIMEZONE });
  if (!londonDateTime.isValid) {
    console.error('Invalid date/time:', `${dateStr}T${time}`, londonDateTime.invalidReason);
    return '';
  }
  
  const utcDateTime = londonDateTime.toUTC();
  return utcDateTime.toISO() || '';
};

/**
 * Convert a UTC ISO string to London time (HH:mm) for display.
 * This is used when reading shift data from the database.
 */
export const convertUTCToLondonTime = (utcIsoString: string): string => {
  const utcDateTime = DateTime.fromISO(utcIsoString, { zone: 'utc' });
  if (!utcDateTime.isValid) {
    console.error('Invalid UTC ISO string:', utcIsoString, utcDateTime.invalidReason);
    return '';
  }
  
  const londonTime = utcDateTime.setZone(DEFAULT_TIMEZONE).toFormat('HH:mm');
  return londonTime;
};

/**
 * Convert a UTC ISO string to a Date object in London timezone.
 * This is used for date operations that need to work with London time.
 */
export const convertUTCToLondonDate = (utcIsoString: string): Date => {
  const utcDateTime = DateTime.fromISO(utcIsoString, { zone: 'utc' });
  return utcDateTime.setZone(DEFAULT_TIMEZONE).toJSDate();
};

/**
 * Parse a date string (YYYY-MM-DD) and return a Date object in London timezone.
 */
export const parseDateString = (dateStr: string): Date => {
  return DateTime.fromISO(dateStr, { zone: DEFAULT_TIMEZONE }).toJSDate();
};